import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import {
  User, Phone, Calendar as CalendarIcon, DollarSign, 
  TrendingUp, Plus, Wallet, Coffee, Home, Truck, Edit3, Save, X, Clock, Trash2, CheckCircle
} from "lucide-react";
import toast from "react-hot-toast";

export default function SupervisorDetails() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [supervisor, setSupervisor] = useState({});
  const [logs, setLogs] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  
  // UI States
  const [showCalendar, setShowCalendar] = useState(false);
  const [isEditingSalary, setIsEditingSalary] = useState(false);
  const [newSalary, setNewSalary] = useState(0);
  const [editingLogId, setEditingLogId] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  const [stats, setStats] = useState({
    totalFood: 0,
    totalAdvance: 0,
    totalSalaryPaid: 0,
    pendingSalary: 0,
    totalLeaves: 0,
    totalExpenses: 0,
    currentMonthPresent: 0
  });

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    work_done_description: "",
    food_expenses: 0,
    advance_taken: 0,
    room_expenses: 0,
    travel_expenses: 0,
    other_expenses: 0,
    salary_credited: 0, // Added explicit salary tracking state
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: sup } = await supabase.from("supervisors").select("*").eq("id", id).single();
      const { data: logsData } = await supabase.from("supervisor_logs")
        .select("*")
        .eq("supervisor_id", id)
        .order("date", { ascending: false });
      const { data: attData } = await supabase.from("attendance")
        .select("date")
        .eq("supervisor_id", id);

      setSupervisor(sup);
      setNewSalary(sup?.base_monthly_salary || 0);
      setLogs(logsData || []);
      setAttendanceRecords(attData?.map(a => a.date) || []);
      calculateStats(sup, logsData || [], attData || []);
    } catch (error) {
      toast.error("Error loading data");
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (sup, logsData, attData) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const totals = logsData.reduce((acc, curr) => {
      acc.food += Number(curr.food_expenses || 0);
      acc.advance += Number(curr.advance_taken || 0);
      acc.salaryPaid += Number(curr.salary_credited || 0);
      acc.others += Number(curr.room_expenses || 0) + Number(curr.travel_expenses || 0) + Number(curr.other_expenses || 0);
      return acc;
    }, { food: 0, advance: 0, salaryPaid: 0, others: 0 });

    const monthPresent = attData.filter(a => {
        const d = new Date(a.date);
        return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }).length;

    // Financial Formulas:
    // Net Payable Salary = (Contract Base Salary) - (Advance Deductions Taken) - (Direct Payouts Already Credited)
    const netContractSalary = Number(sup?.base_monthly_salary || 0);
    const calculatedPendingBalance = netContractSalary - totals.advance - totals.salaryPaid;

    setStats({
      totalFood: totals.food,
      totalAdvance: totals.advance,
      totalSalaryPaid: totals.salaryPaid,
      totalLeaves:0,
      pendingSalary: calculatedPendingBalance,
      totalExpenses: totals.food + totals.others,
      currentMonthPresent: monthPresent
    });
  };

  // --- ATTENDANCE LOGIC ---
  const toggleAttendance = async (dateString) => {
    const isPresent = attendanceRecords.includes(dateString);
    try {
      if (isPresent) {
        await supabase.from("attendance").delete().eq("supervisor_id", id).eq("date", dateString);
        setAttendanceRecords(prev => prev.filter(d => d !== dateString));
      } else {
        await supabase.from("attendance").insert([{ supervisor_id: id, date: dateString }]);
        setAttendanceRecords(prev => [...prev, dateString]);
      }
      fetchData(); 
    } catch (err) {
      toast.error("Attendance update failed");
    }
  };

  // --- SALARY CONFIGURATION ---
  const updateSalary = async () => {
    try {
      const { error } = await supabase.from("supervisors").update({ base_monthly_salary: newSalary }).eq("id", id);
      if (error) throw error;
      toast.success("Salary contract updated");
      setIsEditingSalary(false);
      fetchData();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  // --- LOG CRUD ---
  const handleAddLog = async () => {
    try {
      const now = new Date();
      const timeString = now.toTimeString().split(" ")[0];
      
      const { error } = await supabase.from("supervisor_logs").insert([
        { 
          supervisor_id: id, 
          ...formData, 
          date: `${formData.date}T${timeString}` 
        },
      ]);
      if (error) throw error;
      toast.success("Log record created successfully");
      setShowForm(false);
      setFormData({
        date: new Date().toISOString().split("T")[0],
        work_done_description: "",
        food_expenses: 0,
        advance_taken: 0,
        room_expenses: 0,
        travel_expenses: 0,
        other_expenses: 0,
        salary_credited: 0,
      });
      fetchData();
    } catch (err) {
      toast.error("Failed to add log");
    }
  };

  const handleUpdateLog = async () => {
    try {
      const { error } = await supabase.from("supervisor_logs").update(editFormData).eq("id", editingLogId);
      if (error) throw error;
      toast.success("Record changes saved");
      setEditingLogId(null);
      fetchData();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm("Delete this record permanently?")) return;
    try {
      await supabase.from("supervisor_logs").delete().eq("id", logId);
      toast.success("Deleted");
      fetchData();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  if (loading || !supervisor) return <div className="p-10 text-center font-black animate-pulse text-[#0054A6]">LOADING...</div>;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
      
      {/* 🔷 HEADER SECTION */}
      <div className="bg-[#0054A6] p-8 rounded-[2.5rem] text-white shadow-xl flex flex-col md:flex-row justify-between items-start gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-3">
             <h1 className="text-3xl font-black">{supervisor?.name}</h1>
             <div className="bg-green-400 text-[#0054A6] text-[10px] px-2 py-1 rounded-lg font-black uppercase">Active</div>
          </div>
          
          <div className="flex flex-wrap gap-4 mt-4">
            <div className="flex items-center gap-2 text-xs font-bold bg-white/10 px-3 py-1.5 rounded-full">
              <Phone size={14} /> {supervisor?.phone || "No Phone"}
            </div>
            
            <button 
                onClick={() => setShowCalendar(true)}
                className="flex items-center gap-2 text-xs font-black bg-white text-[#0054A6] px-4 py-1.5 rounded-full hover:bg-green-50 transition-colors shadow-lg"
            >
              <CheckCircle size={14} /> Mark Attendance
            </button>

            <div className="flex items-center gap-2 text-xs font-bold bg-white/10 px-3 py-1.5 rounded-full text-green-300">
              <TrendingUp size={14} /> {stats.currentMonthPresent} Days This Month
            </div>
          </div>
        </div>

        <div className="bg-white/10 p-5 rounded-3xl backdrop-blur-md min-w-[220px] border border-white/20">
          <div className="flex justify-between items-center mb-1">
            <p className="text-[10px] uppercase font-black text-blue-200 tracking-widest">Monthly Base Salary</p>
            <button onClick={() => setIsEditingSalary(!isEditingSalary)} className="text-blue-200 hover:text-white">
               {isEditingSalary ? <X size={14}/> : <Edit3 size={14}/>}
            </button>
          </div>
          {isEditingSalary ? (
            <div className="flex gap-2 mt-2">
              <input type="number" value={newSalary} onChange={(e) => setNewSalary(e.target.value)} className="bg-white/20 rounded-lg px-2 py-1 w-full text-lg font-bold outline-none border border-white/30 text-white" />
              <button onClick={updateSalary} className="bg-green-500 p-2 rounded-lg hover:bg-green-600 transition-colors"><Save size={16}/></button>
            </div>
          ) : (
            <p className="text-2xl font-black mt-1">₹{Number(supervisor.base_monthly_salary || 0).toLocaleString()}</p>
          )}
        </div>
      </div>

      {/* 🔷 STATS CARDS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Net Balance Due" value={stats.pendingSalary} color={stats.pendingSalary < 0 ? "text-orange-600" : "text-red-500"}  icon={<Wallet size={16}/>} />
        <StatCard label="Total Advance Taken" value={stats.totalAdvance} color="text-orange-500"  icon={<DollarSign size={16}/>} />
        <StatCard label="Salary Paid Out" value={stats.totalSalaryPaid} color="text-blue-600"  icon={<CheckCircle size={16}/>} />
        <StatCard label="Food Expenses" value={stats.totalFood} color="text-emerald-600" icon={<Coffee size={16}/>} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white p-6 rounded-[2rem] border shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest">Expense & Advance History</h3>
            <button onClick={() => setShowForm(!showForm)} className="bg-[#0054A6] text-white px-4 py-2 rounded-xl text-xs font-black shadow-lg">
              <Plus size={14} className="inline mr-1" /> NEW LOG / CREDIT
            </button>
          </div>

          {showForm && (
            <div className="mb-6 p-6 bg-blue-50/30 rounded-[1.5rem] border border-blue-100 space-y-4 animate-in fade-in zoom-in-95">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Log Date</label>
                      <input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} className="p-3 rounded-xl ring-1 ring-slate-200 outline-none font-bold text-sm bg-white" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold uppercase text-slate-400 ml-1">Remarks / Work Description</label>
                      <input placeholder="E.g., Site clearing / Payout reference" value={formData.work_done_description} onChange={(e) => setFormData({...formData, work_done_description: e.target.value})} className="p-3 rounded-xl ring-1 ring-slate-200 outline-none font-bold text-sm bg-white" />
                    </div>
                </div>
                
                <div className="border-t border-slate-200/60 my-2 pt-2">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Transaction Values (₹)</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <input type="number" placeholder="Food Expense" onChange={(e) => setFormData({...formData, food_expenses: e.target.value})} className="p-3 rounded-xl ring-1 ring-slate-200 text-sm font-bold bg-white" />
                      <input type="number" placeholder="Room Expense" onChange={(e) => setFormData({...formData, room_expenses: e.target.value})} className="p-3 rounded-xl ring-1 ring-slate-200 text-sm font-bold bg-white" />
                      <input type="number" placeholder="Travel Expense" onChange={(e) => setFormData({...formData, travel_expenses: e.target.value})} className="p-3 rounded-xl ring-1 ring-slate-200 text-sm font-bold bg-white" />
                      <input type="number" placeholder="Other Expense" onChange={(e) => setFormData({...formData, other_expenses: e.target.value})} className="p-3 rounded-xl ring-1 ring-slate-200 text-sm font-bold bg-white" />
                      
                      {/* Operational Inputs */}
                      <input type="number" placeholder="Advance Taken (-)" onChange={(e) => setFormData({...formData, advance_taken: e.target.value})} className="p-3 rounded-xl ring-1 ring-red-100 bg-red-50/50 text-red-600 text-sm font-bold" />
                      <input type="number" placeholder="Salary Credited (+)" onChange={(e) => setFormData({...formData, salary_credited: e.target.value})} className="p-3 rounded-xl ring-1 ring-green-100 bg-green-50/50 text-green-600 text-sm font-bold" />
                  </div>
                </div>

                <div className="flex justify-end gap-2 pt-2">
                   <button onClick={handleAddLog} className="bg-[#0054A6] text-white px-6 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-md">Confirm Transaction</button>
                </div>
            </div>
          )}

          <div className="space-y-3">
            {logs.map((log) => (
              <div key={log.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col gap-4">
                  {editingLogId === log.id ? (
                      <div className="grid grid-cols-2 gap-3 animate-in fade-in">
                          <input type="date" value={editFormData.date?.split('T')[0]} onChange={(e) => setEditFormData({...editFormData, date: e.target.value})} className="p-2 rounded-lg ring-1 ring-slate-200 text-xs font-bold" />
                          <input value={editFormData.work_done_description} onChange={(e) => setEditFormData({...editFormData, work_done_description: e.target.value})} className="p-2 rounded-lg ring-1 ring-slate-200 text-xs font-bold" />
                          <div className="flex flex-wrap gap-2 col-span-2">
                             <input type="number" value={editFormData.food_expenses} onChange={(e) => setEditFormData({...editFormData, food_expenses: e.target.value})} className="p-2 rounded-lg ring-1 ring-slate-200 text-xs font-bold w-[22%]" placeholder="Food" />
                             <input type="number" value={editFormData.room_expenses} onChange={(e) => setEditFormData({...editFormData, room_expenses: e.target.value})} className="p-2 rounded-lg ring-1 ring-slate-200 text-xs font-bold w-[22%]" placeholder="Room" />
                             <input type="number" value={editFormData.advance_taken} onChange={(e) => setEditFormData({...editFormData, advance_taken: e.target.value})} className="p-2 rounded-lg ring-1 ring-red-200 text-xs font-bold w-[22%]" placeholder="Advance" />
                             <input type="number" value={editFormData.salary_credited} onChange={(e) => setEditFormData({...editFormData, salary_credited: e.target.value})} className="p-2 rounded-lg ring-1 ring-green-200 text-xs font-bold w-[22%]" placeholder="Salary Paid" />
                             
                             <div className="flex gap-1 w-full justify-end mt-2">
                               <button onClick={handleUpdateLog} className="bg-green-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 text-xs font-bold"><Save size={12}/> Save</button>
                               <button onClick={() => setEditingLogId(null)} className="bg-slate-400 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 text-xs font-bold"><X size={12}/> Cancel</button>
                             </div>
                          </div>
                      </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${Number(log.salary_credited) > 0 ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-[#0054A6]'}`}><Clock size={18}/></div>
                            <div>
                                <p className="font-black text-sm text-slate-800">{new Date(log.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                <p className="text-[10px] text-slate-400 font-bold uppercase truncate max-w-[200px]">{log.work_done_description || (Number(log.salary_credited) > 0 ? "Salary Disbursal" : "Expense Log")}</p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-4 items-center justify-between sm:justify-end">
                            {Number(log.food_expenses) > 0 && <LogSubStat label="Food" val={log.food_expenses} />}
                            {Number(log.room_expenses || log.travel_expenses || log.other_expenses) > 0 && (
                              <LogSubStat label="Other Exp" val={Number(log.room_expenses || 0) + Number(log.travel_expenses || 0) + Number(log.other_expenses || 0)} />
                            )}
                            {Number(log.advance_taken) > 0 && <LogSubStat label="Advance" val={log.advance_taken} color="text-red-500" />}
                            {Number(log.salary_credited) > 0 && <LogSubStat label="Salary Paid" val={log.salary_credited} color="text-green-600" />}
                            
                            <div className="flex gap-1 border-l pl-2 border-slate-200">
                                <button onClick={() => {setEditingLogId(log.id); setEditFormData(log);}} className="p-2 text-slate-300 hover:text-blue-600"><Edit3 size={14}/></button>
                                <button onClick={() => handleDeleteLog(log.id)} className="p-2 text-slate-300 hover:text-red-600"><Trash2 size={14}/></button>
                            </div>
                        </div>
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>

        {/* Right Sidebar Totals Panel */}
        <div className="space-y-6">
            <div className="bg-slate-900 p-8 rounded-[2.5rem] text-white space-y-6 shadow-xl">
                <h3 className="text-xs font-black uppercase text-slate-500 tracking-widest">Financial Ledger Breakdown</h3>
                <div className="space-y-4">
                    <BreakdownRow label="Base Contract Rate" value={Number(supervisor.base_monthly_salary || 0)} />
                    <BreakdownRow label="Advance Deductions (-)" value={stats.totalAdvance} color="text-red-400" />
                    <BreakdownRow label="Salary Transferred (-)" value={stats.totalSalaryPaid} color="text-green-400" />
                    <BreakdownRow label="Total Leaves" value={stats.totalLeaves} color="text-blue-400" />
                    <div className="border-t border-white/10 pt-2">
                       <BreakdownRow label="Total Site Food Costs" value={stats.totalFood} />
                    </div>
                </div>
                <div className="pt-6 border-t border-white/10">
                    <p className="text-[10px] font-black text-slate-500 uppercase mb-1">Net Balance Remaining Due</p>
                    <p className={`text-3xl font-black ${stats.pendingSalary < 0 ? 'text-orange-400' : 'text-emerald-400'}`}>
                      ₹{stats.pendingSalary.toLocaleString()}
                    </p>
                </div>
            </div>
            
            <div className="bg-blue-50 p-6 rounded-[2rem] border border-blue-100">
                <h4 className="text-xs font-black text-blue-900 uppercase mb-3 flex items-center gap-2"><TrendingUp size={14}/> Operational Status</h4>
                <p className="text-sm text-blue-800 font-medium leading-relaxed">
                    Logged active attendance for <b>{stats.currentMonthPresent} shifts</b> during this calendar month.
                </p>
            </div>
        </div>
      </div>

      {/* 🔷 ATTENDANCE CALENDAR MODAL */}
      {showCalendar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl overflow-hidden">
            <div className="bg-[#0054A6] p-6 text-white flex justify-between items-center">
               <h2 className="text-xl font-black">Mark Attendance</h2>
               <button onClick={() => setShowCalendar(false)} className="hover:rotate-90 transition-transform"><X /></button>
            </div>
            <div className="p-8 grid grid-cols-7 gap-2 text-center">
                {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => <div key={d} className="text-[10px] font-black text-slate-300 mb-2 uppercase">{d}</div>)}
                {Array.from({length: 31}, (_, i) => {
                    const day = i + 1;
                    const dateStr = `2026-05-${day.toString().padStart(2, '0')}`;
                    const isPresent = attendanceRecords.includes(dateStr);
                    return (
                        <button 
                            key={day}
                            onClick={() => toggleAttendance(dateStr)}
                            className={`aspect-square rounded-xl text-xs font-black transition-all ${isPresent ? 'bg-green-500 text-white shadow-lg shadow-green-100' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}
                        >
                            {day}
                        </button>
                    );
                })}
            </div>
            <div className="px-8 pb-8 text-center">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">Tap dates to toggle presence (Green = Present)</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- HELPERS ---
function StatCard({ label, value, color, icon }) {
  return (
    <div className="bg-white p-5 rounded-3xl border shadow-sm flex flex-col justify-between hover:shadow-md transition-shadow">
      <div className="flex items-center gap-2 text-slate-400 mb-2">{icon}<p className="text-[10px] font-black uppercase tracking-wider">{label}</p></div>
      <p className={`text-xl font-black ${color}`}>{value}</p>
    </div>
  );
}
function LogSubStat({ label, val, color = "text-slate-700" }) {
  return (
    <div className="text-center min-w-[55px]">
      <p className="text-[9px] font-black text-slate-400 uppercase">{label}</p>
      <p className={`text-xs font-black ${color}`}>₹{Number(val || 0).toLocaleString()}</p>
    </div>
  );
}
function BreakdownRow({ label, value, color = "text-white" }) {
  return (
    <div className="flex justify-between items-center text-sm font-bold">
      <span className="text-slate-400">{label}</span>
      <span className={color}>₹{value.toLocaleString()}</span>
    </div>
  );
}